import Box from '@mui/material/Box';
import Skeleton from '@mui/material/Skeleton';
import ContentBox from '@root/components/v2/content/ContentBox';
import Tooltip from '@root/components/v2/ui-elements/Tooltip';
import { useTheme } from '@root/context/ThemeContext';
import { CATEGORICAL_COLORS, SEVERITY_COLORS } from '@root/utilities/pastelColors';
import { Bug, Globe, Info, Skull } from 'lucide-react';

// Headline CVE counts for the Kubernetes vulnerabilities dashboard: all CVEs, then the subsets that
// are exposed at runtime and that are exploitable at runtime. One card, three equal columns, in the
// shared pastel palette (utilities/pastelColors).

const STATS = [
  { key: 'total', label: 'Total Unique CVEs', icon: Bug, color: CATEGORICAL_COLORS.amber },
  {
    key: 'runtime_exposure',
    label: 'Runtime exposure CVEs',
    icon: Globe,
    color: CATEGORICAL_COLORS.lightBlue,
    info: 'CVEs in running workloads that are reachable from the internet.',
  },
  {
    key: 'runtime_exploit',
    label: 'Runtime exploit CVEs',
    icon: Skull,
    color: SEVERITY_COLORS.critical,
    info: 'CVEs with a known exploit in packages that are loaded at runtime.',
  },
];

const BODY_STYLE = {
  height: '100%',
  display: 'grid',
  gridTemplateColumns: `repeat(${STATS.length}, minmax(0, 1fr))`,
  alignItems: 'center',
  padding: '16px',
};

const StatSkeleton = ({ color }) => (
  <Box display="flex" flexDirection="column" alignItems="center" gap="12px">
    <Box display="flex" alignItems="center" gap="10px">
      <Skeleton variant="circular" width={36} height={36} sx={{ bgcolor: color }} />
      <Skeleton variant="text" width={64} height={48} sx={{ bgcolor: color }} />
    </Box>
    <Skeleton variant="text" width={140} height={20} sx={{ bgcolor: color }} />
  </Box>
);

const Stat = ({ stat, value }) => {
  const Icon = stat.icon;

  return (
    <Box display="flex" flexDirection="column" alignItems="center" gap="12px" minWidth={0} textAlign="center">
      <Box display="flex" alignItems="center" gap="10px">
        <Icon size={36} strokeWidth={1.5} color={stat.color} style={{ flexShrink: 0 }} />
        <Box component="span" sx={{ fontSize: '40px', fontWeight: 500, lineHeight: 1, color: stat.color }}>
          {(value ?? 0).toLocaleString()}
        </Box>
      </Box>
      <Box
        display="flex"
        alignItems="center"
        gap="4px"
        maxWidth="100%"
        sx={{ fontSize: '15px', fontWeight: 500, color: 'var(--text-primary)' }}>
        <Box component="span" sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {stat.label}
        </Box>
        {stat.info && (
          // A plain span, not a Box: the Tooltip passes `width="100%"` to its child, which Box would
          // apply as CSS and squeeze the label out of the row.
          <Tooltip title={stat.info} isIcon>
            <span style={{ display: 'inline-flex', flexShrink: 0, color: 'var(--text-secondary)' }}>
              <Info size={14} />
            </span>
          </Tooltip>
        )}
      </Box>
    </Box>
  );
};

const CveSummaryCard = ({ data, isLoading = false, height = '300px' }) => {
  const { isDarkMode } = useTheme();
  const skeletonColor = isDarkMode ? '#2d3748' : undefined;

  return (
    <ContentBox
      // containerStyle={{ border: 'none' }}
      containerClassName={'border-box-no-pad'}
      paddingMode="content"
      height={height}>
      <Box sx={BODY_STYLE}>
        {STATS.map(stat =>
          isLoading ? (
            <StatSkeleton key={stat.key} color={skeletonColor} />
          ) : (
            <Stat key={stat.key} stat={stat} value={data?.[stat.key]} />
          ),
        )}
      </Box>
    </ContentBox>
  );
};

export default CveSummaryCard;
